
import moment from "moment";
import { useMemo } from "react";
import { useSelector } from "react-redux";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import { Autocomplete, AutocompleteAPI, DateTimePicker, FormField, Typography } from "@maysoft/common-component-react";

import Helpers from "commons/helpers";
import Strings from "constants/strings";
import useDataCommon from "hooks/useDataCommon.hook";

import Constants from "constants/index";
import { RootState } from "store";
import { Mode, ReportStatus, Status } from "constants/enum";
import { ICodename, IDataDetailAnalysis, IErrorAnalysis } from "commons/interfaces";



export const CardIncidentResolutionMonitoring = ({
    mode,
    dataOptions,
    dataAnalysis,
    errorAnalysis,
    handleChangeValue,
}: {
    mode: Mode,
    errorAnalysis: IErrorAnalysis;
    dataAnalysis: IDataDetailAnalysis;
    dataOptions: { reportStatus: ICodename[]; };
    handleChangeValue: (key: string, value: any) => void;
}) => {

    const { getDataUsers } = useDataCommon();
    const user = useSelector((state: RootState) => state.user);

    const requestGetApiUser = useMemo(() => ({
        listStatus: [Status.Active],
        organizationId: user.workingOrganization || "0",
    }), [user.workingOrganization]);

    return (
        <Card sx={{ padding: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Typography variant="h6">
                        {Strings.ANALYSIS.INCIDENT_RESOLUTION_MONITORING}
                    </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                    <Autocomplete
                        mode={mode}
                        variant={"outlined"}
                        data={dataOptions.reportStatus.filter(codename => codename.code === ReportStatus.processing || codename.code === ReportStatus.processed)}
                        label={Strings.ANALYSIS.REPORT_STATUS}
                        defaultValue={dataAnalysis?.reportStatus}
                        errorMessage={errorAnalysis?.reportStatus}
                        placeholder={Strings.ANALYSIS.SELECT_REPORT_STATUS}
                        onChange={(value) => {
                            handleChangeValue("reportStatus", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AutocompleteAPI
                        mode={mode}
                        variant={"outlined"}
                        getApi={(requestGetApi) => getDataUsers(requestGetApi, true)}
                        requestGetApi={requestGetApiUser}
                        label={Strings.ANALYSIS.INCHARGED_STAFF}
                        defaultValue={dataAnalysis?.inchargedStaff}
                        errorMessage={errorAnalysis?.inchargedStaff}
                        placeholder={Strings.ANALYSIS.SELECT_INCHARGED_STAFF}
                        dataSelected={dataAnalysis?.inchargedStaff ? [{
                            code: dataAnalysis?.inchargedStaff || "",
                            name: dataAnalysis?.inchargedStaffName || "",
                        }] : []}
                        onChange={(value) => { }}
                        onChangeItem={(value) => {
                            handleChangeValue("inchargedStaff", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.ANALYSIS.INCHARGED_STAFF_TITLE}
                        defaultValue={dataAnalysis?.inchargedStaffTitle}
                        errorMessage={errorAnalysis?.inchargedStaffTitle}
                        placeholder={Strings.ANALYSIS.ENTER_INCHARGED_STAFF_TITLE}
                        onBlur={(value) => {
                            handleChangeValue("inchargedStaffTitle", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <AutocompleteAPI
                        mode={mode}
                        variant={"outlined"}
                        getApi={(requestGetApi) => getDataUsers(requestGetApi, true)}
                        requestGetApi={requestGetApiUser}
                        label={Strings.ANALYSIS.COORDINATED_STAFF}
                        defaultValue={dataAnalysis?.coordinatedStaff}
                        errorMessage={errorAnalysis?.coordinatedStaff}
                        placeholder={Strings.ANALYSIS.SELECT_COORDINATED_STAFF}
                        dataSelected={dataAnalysis?.coordinatedStaff ? [{
                            code: dataAnalysis?.coordinatedStaff || "",
                            name: dataAnalysis?.coordinatedStaffName || "",
                        }] : []}
                        onChange={(value) => { }}
                        onChangeItem={(value) => {
                            handleChangeValue("coordinatedStaff", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.ANALYSIS.COORDINATED_STAFF_TITLE}
                        defaultValue={dataAnalysis?.coordinatedStaffTitle}
                        errorMessage={errorAnalysis?.coordinatedStaffTitle}
                        placeholder={Strings.ANALYSIS.ENTER_COORDINATED_STAFF_TITLE}
                        onBlur={(value) => {
                            handleChangeValue("coordinatedStaffTitle", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        multiline
                        mode={mode}
                        maxRows={10}
                        variant={"outlined"}
                        maxLength={Constants.CHAR_MAX_LENGTH.REPORT}
                        label={Strings.ANALYSIS.IMPLEMENT_PLAN + ` (${Strings.formatString(Strings.MESSAGE.CHAR_MAX_LENGTH, Constants.CHAR_MAX_LENGTH.REPORT)})`}
                        defaultValue={dataAnalysis?.implementPlan}
                        errorMessage={errorAnalysis?.implementPlan}
                        placeholder={Strings.ANALYSIS.ENTER_IMPLEMENT_PLAN}
                        onBlur={(value) => {
                            handleChangeValue("implementPlan", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <FormField
                        multiline
                        mode={mode}
                        maxRows={10}
                        variant={"outlined"}
                        label={Strings.ANALYSIS.IMPLEMENT_RESULT}
                        defaultValue={dataAnalysis?.implementResult}
                        errorMessage={errorAnalysis?.implementResult}
                        placeholder={Strings.ANALYSIS.ENTER_IMPLEMENT_RESULT}
                        onBlur={(value) => {
                            handleChangeValue("implementResult", value);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimePicker
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.ANALYSIS.DUE_DATE}
                        errorMessage={errorAnalysis?.duedate}
                        value={Helpers.toUnix(dataAnalysis?.duedate)}
                        onChangeValue={(value) => {
                            const newValue = value ? moment(value).unix() : undefined;
                            handleChangeValue("duedate", newValue);
                        }}
                    />
                </Grid>
                <Grid item xs={12} sm={6}>
                    <DateTimePicker
                        mode={mode}
                        variant={"outlined"}
                        label={Strings.ANALYSIS.SCHEDULE_FINISH_DATE}
                        errorMessage={errorAnalysis?.scheduleFinishDate}
                        value={Helpers.toUnix(dataAnalysis?.scheduleFinishDate)}
                        onChangeValue={(value) => {
                            const newValue = value ? moment(value).unix() : undefined;
                            handleChangeValue("scheduleFinishDate", newValue);
                        }}
                    />
                </Grid>
            </Grid>
        </Card>
    );
};

export default CardIncidentResolutionMonitoring;
